import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Header, LoadingSpinner, Empty, TitleHeader, XappTable } from "@xapp/components";
import { Template } from "@xapp/ovai-lib";
import { UserProfile } from "auth/modules/state";
import { FormFields, NewAppForm } from "components/App";
import { PromptContainer } from "components/AppOverview/components/PromptContainer";
import { DashboardDateFilter, useDashboardDateFilter } from "components/DashboardDateFilter";
import { Section } from "components/Layout";
import { OrgDashboardContainer } from "components/OrgDashboard";
import { OrganizationSecondaryNav } from "components/OrgSecondaryNav";
import { PageHeader } from "components/PageHeader";
import { RowModel } from "components/StentorUIComponents";
import { BETA_RECOMENDATIONS, BETA_FLEET_MANAGEMENT, BETA_OVERVIEW_V2 } from "constants/beta";
import { Organization } from "generated/graphql";
import { ClientApp } from "models";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "react-toolbox/lib/dialog";
import { Cell, Pie, PieChart } from "recharts";
import { State } from "redux/state";
import { fetchUsersOfOrganization } from "routes/Organization/modules/actions";
import { Pagination } from "components/Pagination";
import { getTotalPages } from "components/Pagination/Pagination";
import { getAvailableTemplateTypes } from "utils/account";
import { CUSTOM_TYPE, OC_STUDIO_STARTER_TEMPLATE } from "constants/templates";
import { OrgRecentAnalytics } from "../containers/OrgRecentAnalytics";

import {
    AppsTableColumn,
    AppsTableData,
    AppStatus,
    DEFAULT_COLUMNS,
    TABLE_COLUMN_HEADER_LABELS,
} from "../Types";
import { renderAppsTableRow } from "./AppsTableRow";
import { StatusSelection } from "./StatusSelection";
import { useBoolState } from "utils/bool";
import { QueryStringPaging } from "components/QueryStringPaging";
import { PageSizeSelector } from "components/PagingList/PageSizeSelector";
import { OrderByType } from "../containers/OrgOverviewContainer";
import { SnippetDialog } from "components/AppChannels/Dialogs/SnippetDialog";
import style from "./style.scss";

export interface OrgAppsTotals {
    /**
     * Total number of apps
     */
    apps: number;
    /**
     * Apps cancelled
     */
    cancelled: number;
    /**
     * Apps archived
     */
    archived: number;
    /**
     * Apps that require attention.
     */
    requiresAttention: number;
    /**
     * Apps live
     */
    live: number;
    /**
     * Apps ready
     */
    ready: number;
    /**
     * Apps in review
     */
    inReview: number;
    /**
     * Apps submitted
     */
    submitted: number;
    /**
     * Apps incomplete
     */
    incomplete: number;
}

export interface AppTableProps {
    /**
     * Current table data for display
     */
    readonly tableData?: AppsTableData[];
    /**
     * Is the table loading
     */
    readonly tableLoading?: boolean;
    /**
     * Current Index for pagination
     */
    readonly fromIndex?: number;
    /**
     * Selected page for pagination
     */
    readonly selectedPage?: number;
    /**
     * Page Size
     */
    readonly pageSize?: number;
    /**
     * Total items within current query
     *
     * Used to display total items
     */
    readonly totalItems?: number;
    /**
     * How to order the results
     */
    readonly sortValue?: OrderByType;
    /**
     * User selects a new page
     * @param page
     */
    onSelectPage?(page: number): void;
    /**
     * User changes the page size
     *
     * @param pageSize
     * @returns
     */
    onPageSizeChange?: (pageSize: number) => void;
    /**
     * User changes the sort
     * @param value
     * @returns
     */
    onSortClick?: (value: OrderByType) => void;
}

export interface OrgOverviewProps {

    // General Org
    readonly org: Organization;
    readonly appId?: string;
    readonly totals: OrgAppsTotals;
    readonly user: UserProfile;
    /**
     * When true, only render the management section (skip analytics/recommendations/header)
     */
    readonly managementOnly?: boolean;

    onCreateApp(app: ClientApp): void;
    onUploadTemplate(app: Template): void;

    onActionClick?(status: string, appId: string): void;

    // Analytics Controls, Optional
    readonly endDate?: Date;
    readonly startDate?: Date;
    onChangeStartDate?(val: Date): void;
    onChangeEndDate?(val: Date): void;

    readonly activeStatus?: AppStatus;
    onSelectStatus?(status: AppStatus): void;

    valueNotes?: (note: string, id: string, status: string) => void;
    readonly prioritize?: boolean;
    /**
     * The table data for selected status
     */
    readonly tableData?: AppTableProps;
}

export const OrgOverviewView: FC<OrgOverviewProps> = ({
    org,
    user,
    activeStatus,
    startDate,
    endDate,
    onCreateApp,
    onUploadTemplate,
    onSelectStatus,
    onActionClick,
    totals,
    valueNotes,
    tableData,
    managementOnly
}: OrgOverviewProps) => {

    const [showCreateAppDialog, setShowCreateAppDialog] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { logoUrl, name, beta, organizationId } = org;
    const {
        selectedPage,
        sortValue,
        onSortClick,
        totalItems,
        tableLoading,
        fromIndex,
        onSelectPage,
        pageSize,
        onPageSizeChange,
        tableData: appsTableData
    } = tableData

    const totalApps = totals.apps;

    // Feature Flags
    const displayManagement: boolean = managementOnly || (!!beta && typeof beta === "object" ? !!beta[BETA_FLEET_MANAGEMENT] : false);
    const displayRecommendations: boolean = !managementOnly && (!!beta && typeof beta === "object" ? !!beta[BETA_RECOMENDATIONS] : false);
    const displayOverviewV2: boolean = !managementOnly && (!!beta && typeof beta === "object" ? !!beta[BETA_OVERVIEW_V2] : false);

    // Date filtering for Overview V2 with URL query string persistence
    const location = useLocation();
    const history = useHistory();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const { defaultStartDate, defaultEndDate, parseDateFromString } = useDashboardDateFilter(7);

    // Initialize dates from URL or defaults
    const dashboardStartDate = useMemo(() => {
        return parseDateFromString(searchParams.get('startDate'), defaultStartDate);
    }, [searchParams, parseDateFromString, defaultStartDate]);

    const dashboardEndDate = useMemo(() => {
        return parseDateFromString(searchParams.get('endDate'), defaultEndDate);
    }, [searchParams, parseDateFromString, defaultEndDate]);

    const handleDashboardDateChange = useCallback((start: Date, end: Date) => {
        const newParams = new URLSearchParams(location.search);
        newParams.set('startDate', start.toISOString());
        newParams.set('endDate', end.toISOString());
        history.replace({
            pathname: location.pathname,
            search: `?${newParams.toString()}`
        });
    }, [location.search, location.pathname, history]);

    const state = useSelector<State, State>((state) => state);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchUsersOfOrganization(org.organizationId, { includeRoles: true }));
    }, [org.organizationId, dispatch]);

    const statusAmountList = useMemo(() => {
        return [
            { name: "Live", value: totals.live, color: "#9eed98" },
            { name: "Ready for Install", value: totals.ready, color: "#ff8f00" },
            { name: "In Review", value: totals.inReview, color: "#ffd000" },
            { name: "Requires Attention", value: totals.requiresAttention, color: "#ff0000" },
            {
                name: "Submitted for Review",
                value: totals.submitted,
                color: "#d5806e",
            },
            { name: "Incomplete", value: totals.incomplete, color: "#b0bec5" }
        ];
    }, [totals]);

    const imageTag = useMemo(() => {
        return logoUrl ? <img src={logoUrl} alt={`Logo for ${name}`} /> : "";
    }, [logoUrl, name]);

    const recomendations = useMemo(() => {
        return {
            orgIcon: org.logoUrl,
            numberOfApps: totalApps,
            numberOfMembers: (
                (state.org.currentOrg ? state.org.users[state.org.currentOrg.organizationId] : undefined) || []
            )?.length,
        };
    }, [totalApps, org.logoUrl, state.org.currentOrg, state.org.users]);

    function handleToggleCreateAppDialog() {
        setShowCreateAppDialog((val) => !val);
    }

    const handleCreateAppClick = useCallback(
        (app: ClientApp | Template) => {
            if (app.templateType === CUSTOM_TYPE || app.templateType === OC_STUDIO_STARTER_TEMPLATE) {
                onCreateApp(app as ClientApp);
            } else {
                onUploadTemplate(app as Template);
            }

            handleToggleCreateAppDialog();
        },
        [onCreateApp, onUploadTemplate]
    );

    const renderCreateAppDialog = useCallback(() => {
        const listFormField: FormFields = {
            appId: {
                value: undefined,
            },
            organizationId: {
                disabled: true,
                value: org.organizationId,
            },
            templateType: {
                disabled: false,
                show: true,
                value: undefined,
            },
        };

        return (
            <Dialog active={showCreateAppDialog}>
                <NewAppForm
                    availableTemplates={getAvailableTemplateTypes(user?.isAdmin)}
                    onCancel={handleToggleCreateAppDialog}
                    onSave={handleCreateAppClick}
                    defaultFieldValues={listFormField}
                    organizations={org ? [org] : []}
                />
            </Dialog>
        );
    }, [handleCreateAppClick, org.organizationId, showCreateAppDialog, user?.isAdmin]);

    const TABLE_COLUMN_HEADERS = useMemo<readonly AppsTableColumn[]>(() => {
        const items: readonly Omit<AppsTableColumn, "label">[] = [
            {
                id: "app_name",
            },
            {
                id: "created_date",
            },
            {
                id: "last_modified",
            },
            {
                id: "submitter",
            },
            {
                id: "action",
            },
            {
                id: "notes",
            },
        ];

        return items.map<AppsTableColumn>((data) => ({
            ...data,
            label: TABLE_COLUMN_HEADER_LABELS[data.id],
        }));
    }, []);

    const [snippetDialogActive, hideSnippetDialog, showSnippetDialog] = useBoolState(false);

    const [dialogKey, setDialogKey] = useState<string>("");

    const showSnippetDialogKey = (key: string) => {
        setDialogKey(key);
    };

    const renderSnippetDialog = useCallback(() => {
        const value = {
            key: dialogKey,
        };
        return <SnippetDialog product={"widget"} active={snippetDialogActive} value={value} onClose={hideSnippetDialog} />;
    }, [dialogKey, hideSnippetDialog, snippetDialogActive]);

    const columns: AppsTableColumn[] = useMemo(() => {
        return DEFAULT_COLUMNS.map((id) => {
            const column = TABLE_COLUMN_HEADERS.find((column) => id === column.id);
            return column;
        });
    }, [TABLE_COLUMN_HEADERS]);

    const rowData: RowModel[] = useMemo(() => {
        return appsTableData?.map((app) => {

            return renderAppsTableRow({
                visibleColumns: DEFAULT_COLUMNS,
                appId: app.appId,
                appIcon: app.appIcon,
                appName: app.appName,
                createdDate: app.createdDate,
                lastModifiedDate: app.lastModifiedDate,
                submitter: app.submitter,
                status: activeStatus,
                notes: app.notes,
                onActionClick,
                showSnippetDialog,
                showSnippetDialogKey,
                organizationId,
                setLoading,
                valueNotes,
            });
        });
    }, [activeStatus, appsTableData, onActionClick, organizationId, showSnippetDialog, valueNotes]);

    const renderPageContent = useCallback(() => {
        if (totalApps > 0) {
            return (
                <>
                    {!managementOnly && (displayOverviewV2 ? (
                        <OrgDashboardContainer
                            organizationId={org.organizationId}
                            embedded
                            startDate={dashboardStartDate}
                            endDate={dashboardEndDate}
                        />
                    ) : (
                        <OrgRecentAnalytics organizationId={org.organizationId} endDate={endDate} startDate={startDate} />
                    ))}
                    {displayManagement ? (
                        <>
                            <div>
                                {/* On /management the page header carries the title */}
                                {!managementOnly && <TitleHeader value="Management" static />}
                                <div className={style.statusSectionInner}>
                                    <div>
                                        <StatusSelection
                                            activeStatus={activeStatus}
                                            statusAmountList={statusAmountList}
                                            onSelectStatus={onSelectStatus}
                                            organizationId={organizationId}
                                        />
                                        <small>Cancelled: {totals.cancelled} Archived: {totals.archived}</small>
                                    </div>
                                    <PieChart width={320} height={320}>
                                        <Pie
                                            data={statusAmountList}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={0}
                                            dataKey="value"
                                            isAnimationActive={false}
                                            label
                                        >
                                            {statusAmountList.map((obj) => {
                                                return <Cell key={obj.color} fill={obj.color} />;
                                            })}
                                        </Pie>
                                    </PieChart>
                                </div>
                            </div>
                            <section className={style.appsTableSection}>
                                {activeStatus ? (
                                    rowData.length > 0 ? (
                                        <div className={style.appsTableContainer}>
                                            {loading ? (
                                                <LoadingSpinner loadingText="Loading..." />
                                            ) : (
                                                <>
                                                    <XappTable
                                                        dataColumns={columns}
                                                        dataRows={rowData}
                                                        loading={tableLoading}
                                                        onSortClick={onSortClick}
                                                        sortValue={sortValue}
                                                    />
                                                </>

                                            )}
                                            <div className={style.pagination}>
                                                <div className={style.query}>
                                                    <QueryStringPaging
                                                        currentIndex={fromIndex}
                                                        itemsPerPage={pageSize}
                                                        totalItems={totalItems}
                                                        hideLinks
                                                    />
                                                </div>
                                                <div className={style.pageSize}>
                                                    <label>Items per page: </label>
                                                    <PageSizeSelector
                                                        value={pageSize}
                                                        onChange={onPageSizeChange}
                                                        itemsPerPage={[10, 25, 50, 100]}
                                                    />
                                                </div>
                                                <Pagination
                                                    totalPages={getTotalPages(totalItems, pageSize)}
                                                    page={selectedPage}
                                                    setPage={onSelectPage}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={style.appsTableEmptyContainer}>
                                            <LoadingSpinner loadingText="Loading..." />
                                        </div>
                                    )
                                ) : (
                                    <div className={style.appsTableEmptyContainer}>Please select a status</div>
                                )}
                                {totalApps === 0 && (
                                    <div className={style.appsTableEmptyContainer}>
                                        There are no apps with this status
                                    </div>
                                )}
                            </section>
                        </>
                    ) : (<></>)}
                    {displayRecommendations ? (
                        <section className={style.promptSection}>
                            {Object.values(recomendations).some((el) => !!el === false) && (
                                <PromptContainer
                                    {...recomendations}
                                    orgId={org.organizationId}
                                    fullsize
                                    type="recomendations"
                                />
                            )}
                        </section>
                    ) : (<></>)
                    }
                </>
            );
        }

        const emptyStyleOverrides: React.CSSProperties = {
            backgroundColor: "transparent",
        };

        return (
            <Empty style={emptyStyleOverrides} title="This organization does not have any apps yet">
                <footer id="org_app_list_view_create">
                    <Button variation="primary" label="Create App" onClick={handleToggleCreateAppDialog} />
                </footer>
            </Empty>
        );
    }, [totalApps, org.organizationId, endDate, startDate, managementOnly, displayOverviewV2, displayManagement, activeStatus, statusAmountList, onSelectStatus, organizationId, totals.cancelled, totals.archived, rowData, loading, columns, tableLoading, onSortClick, sortValue, fromIndex, pageSize, totalItems, onPageSizeChange, selectedPage, onSelectPage, displayRecommendations, recomendations, dashboardStartDate, dashboardEndDate]);

    if (managementOnly) {
        // Standard page header + full-width layout, like the dashboards, Analytics and Members.
        return (
            <>
                <OrganizationSecondaryNav organizationId={org.organizationId} />
                <section>
                    <PageHeader title="Management" />
                    {renderPageContent()}
                </section>
                {renderCreateAppDialog()}
                {renderSnippetDialog()}
            </>
        );
    }

    return (
        <>
            <OrganizationSecondaryNav organizationId={org.organizationId} />

            <Section>
                <Header>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                        <div style={{ height: "65px", display: "flex", alignItems: "center" }}>{org.name}</div>
                        {displayOverviewV2 ? (
                            <DashboardDateFilter
                                startDate={dashboardStartDate}
                                endDate={dashboardEndDate}
                                onDateRangeChange={handleDashboardDateChange}
                            />
                        ) : (
                            <div className={style.iconWrap}>{imageTag}</div>
                        )}
                    </div>
                </Header>
            </Section>

            <Section>
                {renderPageContent()}
            </Section>

            {/* Dialogs */}
            {renderCreateAppDialog()}
            {renderSnippetDialog()}
        </>
    );
};
